import { Show, createMemo, createSignal } from "solid-js";
import { A, useParams, useSearchParams } from "@solidjs/router";
import { useApi } from "../../api/provider.tsx";
import { describeError } from "../../api/result.ts";
import { Async } from "../../components/Async.tsx";
import { Band } from "../../components/Band.tsx";
import { CoverageBar } from "../../components/CoverageBar.tsx";
import { Actions, Button, Form, SelectField, TextField, Toggle } from "../../components/Form.tsx";
import { Markdown } from "../../components/Markdown.tsx";
import { Panel } from "../../components/Panel.tsx";
import { SourceTag } from "../../components/SourceTag.tsx";
import { Table } from "../../components/Table.tsx";
import { Tabs } from "../../components/Tabs.tsx";
import { TagEditor, TagList } from "../../components/Tags.tsx";
import { deleteColumn } from "../../components/DeleteButton.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue, type Loader } from "../../lib/async.ts";
import { formatMoment, formatScore } from "../../lib/format.ts";
import type { Answer, Product, ReportSnapshot } from "../../api/domain.ts";
import { historyHref } from "./history.ts";
import { topicNames } from "./report-view.ts";
import styles from "./admin.module.scss";
import { useSession } from "../../session/provider.tsx";

const blankProduct = (): Product => ({
  id: "",
  name: "",
  active: true,
  description: null,
  project_references: [],
  tags: [],
  dossier: null,
});

/** Every product, with a small form for adding one. */
export const ProductsPage = () => {
  const api = useApi();
  const i18n = useI18n();
  const session = useSession();

  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  const [draft, setDraft] = createSignal<Product | undefined>(undefined);

  const save = async (product: Product) => {
    const result = await api.create("products", product);
    if (!result.ok) return;
    setDraft(undefined);
    products.reload();
  };

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().products.title}</h1>
      </div>

      <Panel
        actions={
          <Show when={session.canWrite()}>
            <Button
              label={i18n.t().products.newProduct}
              onClick={() => setDraft(blankProduct())}
            />
          </Show>
        }
      >
        <Async value={products.value()} onRetry={products.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(product) => product.id}
              emptyLabel={
                session.isCustomer() ? i18n.t().portal.noProducts : i18n.t().common.empty
              }
              columns={[
                {
                  key: "name",
                  header: i18n.t().common.name,
                  cell: (product) => <A href={`/products/${product.id}`}>{product.name}</A>,
                },
                {
                  key: "tags",
                  header: i18n.t().products.tags,
                  cell: (product) => (
                    <TagList tags={product.tags} label={i18n.t().products.tagsLabel} />
                  ),
                },
                { key: "id", header: i18n.t().common.identity, cell: (product) => product.id },
                {
                  key: "active",
                  header: i18n.t().common.active,
                  cell: (product) => (product.active ? i18n.t().common.yes : i18n.t().common.no),
                },
                ...(session.canWrite()
                  ? [
                      deleteColumn({
                        header: i18n.t().common.actions,
                        collection: "products",
                        id: (product) => product.id,
                        label: (product) => product.name,
                        onRemoved: products.reload,
                      }),
                    ]
                  : []),
              ]}
            />
          )}
        </Async>

        <Show when={session.canWrite() ? draft() : undefined}>
          {(product) => (
            <Form>
              <TextField
                label={i18n.t().common.name}
                value={product().name}
                onInput={(name) => setDraft({ ...product(), name })}
              />
              <TextField
                label={i18n.t().common.description}
                optional
                multiline
                value={product().description ?? ""}
                onInput={(description) =>
                  setDraft({ ...product(), description: description === "" ? null : description })
                }
              />
              <TagEditor
                label={i18n.t().products.tags}
                hint={i18n.t().products.tagsHint}
                tags={product().tags}
                onChange={(tags) => setDraft({ ...product(), tags })}
                addLabel={i18n.t().products.addTag}
                removeLabel={(tag) => `${i18n.t().products.removeTag}: ${tag}`}
              />
              <Toggle
                label={i18n.t().common.active}
                checked={product().active}
                onToggle={(active) => setDraft({ ...product(), active })}
              />
              <Actions>
                <Button
                  label={i18n.t().common.create}
                  tone="primary"
                  onClick={() => void save(product())}
                />
                <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
              </Actions>
            </Form>
          )}
        </Show>
      </Panel>
    </>
  );
};

/** The sections of one product, in the order the tabs show them. */
const productTabs = ["overview", "bands", "answers", "dossier"] as const;

type ProductTab = (typeof productTabs)[number];

const isProductTab = (candidate: string | undefined): candidate is ProductTab =>
  productTabs.includes(candidate as ProductTab);

/**
 * One product in four tabs: its details, its bands per topic from a chosen
 * view, its whole answer history, and its dossier. The open tab rides in the
 * URL so a pasted link lands on it. An admin edits the details and the dossier
 * in place; a customer reads them.
 */
export const ProductPage = () => {
  const api = useApi();
  const i18n = useI18n();
  const session = useSession();
  const params = useParams<{ id: string }>();
  const [query, setQuery] = useSearchParams<{ tab?: string }>();

  const product = createLoader(() => api.get("products", params.id, { lang: i18n.locale() }));
  const tab = (): ProductTab => (isProductTab(query.tab) ? query.tab : "overview");

  return (
    <>
      <Async value={product.value()} onRetry={product.reload}>
        {(entity) => {
          /* The loader, not the snapshot: an edit must start from what was last saved. */
          const live = () => loadedValue(product.value()) ?? entity;
          return (
            <>
              <div class={styles.heading}>
                <h1>{live().name}</h1>
                <TagList tags={live().tags} label={i18n.t().products.tagsLabel} />
                <p class={styles.lede}>{live().description ?? live().id}</p>
                <Show when={!session.isCustomer()}>
                  <A href={historyHref("product", live().id)}>{i18n.t().history.link}</A>
                </Show>
              </div>

              <Tabs
                label={live().name}
                active={tab()}
                onPick={(next) => setQuery({ tab: next === "overview" ? undefined : next })}
                tabs={[
                  {
                    id: "overview",
                    label: i18n.t().products.overview,
                    content: () => <OverviewTab product={product} fallback={entity} />,
                  },
                  {
                    id: "bands",
                    label: i18n.t().products.bandOverview,
                    content: () => <BandsTab productId={params.id} />,
                  },
                  {
                    id: "answers",
                    label: i18n.t().products.answers,
                    content: () => <AnswersTab productId={params.id} />,
                  },
                  {
                    id: "dossier",
                    label: i18n.t().products.dossier,
                    content: () => <DossierTab product={product} fallback={entity} />,
                  },
                ]}
              />
            </>
          );
        }}
      </Async>
    </>
  );
};

type EditableProps = {
  /** The product's loader; edits are written back into it once saved. */
  product: Loader<Product>;
  /** The snapshot `Async` handed down, read only until the loader holds a value. */
  fallback: Product;
};

/** Saves a partial product and hands back the error text, if the server refused. */
const usePatch = (loader: Loader<Product>, id: () => string) => {
  const api = useApi();
  const i18n = useI18n();
  const [problem, setProblem] = createSignal<string | undefined>(undefined);
  const [busy, setBusy] = createSignal(false);

  const patch = async (fields: Partial<Product>): Promise<boolean> => {
    setProblem(undefined);
    setBusy(true);
    const result = await api.patch("products", id(), fields);
    setBusy(false);
    if (!result.ok) {
      setProblem(describeError(i18n.t(), result.error));
      return false;
    }
    loader.put(result.value);
    return true;
  };

  return { patch, problem, busy };
};

/** Description, tags and reference projects; an admin edits them in a form. */
const OverviewTab = (props: EditableProps) => {
  const i18n = useI18n();
  const session = useSession();
  const live = () => loadedValue(props.product.value()) ?? props.fallback;
  const { patch, problem, busy } = usePatch(props.product, () => live().id);
  const [draft, setDraft] = createSignal<Product | undefined>(undefined);

  const save = async (edited: Product) => {
    const saved = await patch({
      name: edited.name,
      description: edited.description,
      active: edited.active,
      tags: edited.tags,
      project_references: edited.project_references,
    });
    if (saved) setDraft(undefined);
  };

  return (
    <Panel
      title={i18n.t().products.details}
      actions={
        <Show when={session.canWrite() && draft() === undefined}>
          <Button
            label={i18n.t().products.editDetails}
            onClick={() => setDraft({ ...live(), tags: [...live().tags] })}
          />
        </Show>
      }
    >
      <Show
        when={session.canWrite() ? draft() : undefined}
        fallback={<OverviewFacts product={live()} />}
      >
        {(edited) => (
          <Form>
            <TextField
              label={i18n.t().common.name}
              value={edited().name}
              onInput={(name) => setDraft({ ...edited(), name })}
            />
            <TextField
              label={i18n.t().common.description}
              optional
              multiline
              value={edited().description ?? ""}
              onInput={(description) =>
                setDraft({ ...edited(), description: description === "" ? null : description })
              }
            />
            <TagEditor
              label={i18n.t().products.tags}
              hint={i18n.t().products.tagsHint}
              tags={edited().tags}
              onChange={(tags) => setDraft({ ...edited(), tags })}
              addLabel={i18n.t().products.addTag}
              removeLabel={(tag) => `${i18n.t().products.removeTag}: ${tag}`}
            />
            <TextField
              label={i18n.t().products.references}
              optional
              multiline
              value={edited().project_references.join("\n")}
              onInput={(text) =>
                setDraft({
                  ...edited(),
                  project_references: text
                    .split("\n")
                    .map((line) => line.trim())
                    .filter((line) => line !== ""),
                })
              }
            />
            <Toggle
              label={i18n.t().common.active}
              checked={edited().active}
              onToggle={(active) => setDraft({ ...edited(), active })}
            />
            <Show when={problem()}>{(text) => <p class={styles.problem}>{text()}</p>}</Show>
            <Actions>
              <Button
                label={busy() ? i18n.t().common.saving : i18n.t().common.save}
                tone="primary"
                disabled={busy()}
                onClick={() => void save(edited())}
              />
              <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
            </Actions>
          </Form>
        )}
      </Show>
    </Panel>
  );
};

/** The product's facts as a reader sees them. */
const OverviewFacts = (props: { product: Product }) => {
  const i18n = useI18n();

  return (
    <dl class={styles.sheet}>
      <div class={styles.sheetRow}>
        <dt>{i18n.t().common.description}</dt>
        <dd>
          <Show
            when={props.product.description}
            fallback={<span class={styles.note}>{i18n.t().products.noDescription}</span>}
          >
            {(text) => <p>{text()}</p>}
          </Show>
        </dd>
      </div>
      <div class={styles.sheetRow}>
        <dt>{i18n.t().products.tags}</dt>
        <dd>
          <TagList
            tags={props.product.tags}
            label={i18n.t().products.tagsLabel}
            emptyLabel={i18n.t().products.noTags}
          />
        </dd>
      </div>
      <div class={styles.sheetRow}>
        <dt>{i18n.t().products.references}</dt>
        <dd>
          <Show
            when={props.product.project_references.length > 0}
            fallback={<span class={styles.note}>{i18n.t().products.noReferences}</span>}
          >
            <ul class={styles.plainList}>
              {props.product.project_references.map((reference) => (
                <li>{reference}</li>
              ))}
            </ul>
          </Show>
        </dd>
      </div>
      <div class={styles.sheetRow}>
        <dt>{i18n.t().common.active}</dt>
        <dd>{props.product.active ? i18n.t().common.yes : i18n.t().common.no}</dd>
      </div>
    </dl>
  );
};

/** Bands per topic from an evaluation of a chosen view. */
const BandsTab = (props: { productId: string }) => {
  const api = useApi();
  const i18n = useI18n();

  const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
  const [view, setView] = createSignal("");

  const evaluation = createLoader<ReportSnapshot | undefined>(async () => {
    if (view() === "") return { ok: true as const, value: undefined };
    return await api.evaluate({ view: view() });
  });

  const pickView = (next: string) => {
    setView(next);
    void evaluation.reload();
  };

  return (
    <Panel title={i18n.t().products.bandOverview} description={i18n.t().products.viewHint}>
      <Async value={views.value()} onRetry={views.reload}>
        {(rows) => (
          <SelectField
            label={i18n.t().products.chooseView}
            placeholder={i18n.t().common.none}
            choices={rows.map((entry) => ({ id: entry.id, label: entry.label }))}
            value={view()}
            onPick={pickView}
          />
        )}
      </Async>

      <Async value={evaluation.value()} onRetry={evaluation.reload}>
        {(snapshot) => (
          <Show when={snapshot} fallback={<p class={styles.note}>{i18n.t().reports.noReport}</p>}>
            {(loaded) => {
              const evaluated = () =>
                loaded().evaluation.products.find((entry) => entry.product === props.productId);
              const names = () => topicNames(loaded());

              return (
                <Show
                  when={evaluated()}
                  fallback={<p class={styles.note}>{i18n.t().reports.noReport}</p>}
                >
                  {(entry) => (
                    <Table
                      rows={entry().topics}
                      rowKey={(topic) => topic.topic}
                      emptyLabel={i18n.t().common.empty}
                      caption={`${i18n.t().common.score}: ${
                        entry().score === null
                          ? i18n.t().common.notAssessed
                          : formatScore(entry().score ?? 0, i18n.locale())
                      }`}
                      columns={[
                        {
                          key: "topic",
                          header: i18n.t().common.topic,
                          cell: (topic) =>
                            names().find((named) => named.id === topic.topic)?.label ??
                            topic.topic,
                        },
                        {
                          key: "band",
                          header: i18n.t().common.band,
                          cell: (topic) => <Band score={topic.score} band={topic.band} showScore />,
                        },
                        {
                          key: "coverage",
                          header: i18n.t().common.coverage,
                          cell: (topic) => <CoverageBar {...topic.coverage} />,
                        },
                      ]}
                    />
                  )}
                </Show>
              );
            }}
          </Show>
        )}
      </Async>
    </Panel>
  );
};

/** The product's whole answer history, newest first. */
const AnswersTab = (props: { productId: string }) => {
  const api = useApi();
  const i18n = useI18n();
  const session = useSession();

  const questions = createLoader(() => api.list("questions", { lang: i18n.locale() }));
  const answers = createLoader(() => api.list("answers"));

  const mine = createMemo<Answer[]>(() =>
    (loadedValue(answers.value()) ?? [])
      .filter((answer) => answer.product === props.productId)
      .sort((left, right) => right.valid_from - left.valid_from),
  );

  const questionTitle = (id: string): string =>
    (loadedValue(questions.value()) ?? []).find((question) => question.id === id)?.title ?? id;

  return (
    <Panel title={i18n.t().answer.history}>
      <Async value={answers.value()} onRetry={answers.reload}>
        {() => (
          <Table
            rows={mine()}
            rowKey={(answer) => answer.id}
            emptyLabel={i18n.t().answer.noAnswers}
            columns={[
              {
                key: "question",
                header: i18n.t().common.question,
                cell: (answer) => questionTitle(answer.question),
              },
              {
                key: "value",
                header: i18n.t().answer.value,
                cell: (answer) => <AnswerValueCell answer={answer} />,
              },
              {
                key: "source",
                header: i18n.t().common.author,
                cell: (answer) => (
                  <span class={styles.inline}>
                    <SourceTag source={answer.source} confidence={answer.confidence} />
                    {answer.author}
                  </span>
                ),
              },
              {
                key: "from",
                header: i18n.t().answer.validFrom,
                cell: (answer) => formatMoment(answer.valid_from, i18n.locale()),
              },
              {
                key: "to",
                header: i18n.t().answer.validTo,
                cell: (answer) =>
                  answer.valid_to === null ? "" : formatMoment(answer.valid_to, i18n.locale()),
              },
              ...(session.canWrite()
                ? [
                    deleteColumn({
                      header: i18n.t().common.actions,
                      collection: "answers",
                      id: (answer: Answer) => answer.id,
                      onRemoved: answers.reload,
                    }),
                  ]
                : []),
            ]}
          />
        )}
      </Async>
    </Panel>
  );
};

/**
 * The dossier: rendered for every reader, and for an admin also a Markdown
 * editor with a preview beside it. Nothing is written until Save.
 */
const DossierTab = (props: EditableProps) => {
  const i18n = useI18n();
  const session = useSession();
  const live = () => loadedValue(props.product.value()) ?? props.fallback;
  const { patch, problem, busy } = usePatch(props.product, () => live().id);
  const [draft, setDraft] = createSignal<string | undefined>(undefined);
  const [mode, setMode] = createSignal<"write" | "preview">("write");

  const text = () => draft() ?? "";
  const dirty = () => draft() !== undefined && text() !== (live().dossier ?? "");

  const save = async () => {
    const text = draft() ?? "";
    const saved = await patch({ dossier: text.trim() === "" ? null : text });
    if (saved) setDraft(undefined);
  };

  return (
    <Panel
      title={i18n.t().products.dossier}
      actions={
        <Show when={session.canWrite() && draft() === undefined}>
          <Button
            label={i18n.t().products.editDossier}
            onClick={() => {
              setMode("write");
              setDraft(live().dossier ?? "");
            }}
          />
        </Show>
      }
    >
      {/* An empty draft is a legitimate editor state, so the test is on undefined, not on truth. */}
      <Show
        when={session.canWrite() && draft() !== undefined}
        fallback={
          <Show
            when={live().dossier}
            fallback={<p class={styles.note}>{i18n.t().products.noDossier}</p>}
          >
            {(source) => <Markdown source={source()} />}
          </Show>
        }
      >
        <div class={styles.stack}>
          <div class={styles.inline} role="group" aria-label={i18n.t().products.editDossier}>
            <Button
              label={i18n.t().products.write}
              tone={mode() === "write" ? "primary" : "quiet"}
              onClick={() => setMode("write")}
            />
            <Button
              label={i18n.t().products.preview}
              tone={mode() === "preview" ? "primary" : "quiet"}
              onClick={() => setMode("preview")}
            />
            <Show when={dirty()}>
              <span class={styles.note}>{i18n.t().products.unsaved}</span>
            </Show>
          </div>
          <Show
            when={mode() === "write"}
            fallback={
              <Show
                when={text().trim() !== ""}
                fallback={<p class={styles.note}>{i18n.t().products.noDossier}</p>}
              >
                <Markdown source={text()} />
              </Show>
            }
          >
            <TextField
              label={i18n.t().products.dossier}
              hint={i18n.t().products.dossierHint}
              multiline
              value={text()}
              onInput={setDraft}
            />
          </Show>
          <Show when={problem()}>{(message) => <p class={styles.problem}>{message()}</p>}</Show>
          <Actions>
            <Button
              label={busy() ? i18n.t().common.saving : i18n.t().common.save}
              tone="primary"
              disabled={busy() || !dirty()}
              onClick={() => void save()}
            />
            <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
          </Actions>
        </div>
      </Show>
    </Panel>
  );
};

type AnswerValueCellProps = {
  answer: Answer;
};

/** The raw entered value; text answers stay informational and are shown as such. */
const AnswerValueCell = (props: AnswerValueCellProps) => {
  const i18n = useI18n();
  const value = () => props.answer.value;

  return (
    <Show when={value()} fallback={<span class={styles.note}>{i18n.t().common.dontKnow}</span>}>
      {(entered) => (
        <Show
          when={entered().kind !== "text"}
          fallback={<span class={styles.note}>{String(entered().value)}</span>}
        >
          <span>
            {Array.isArray(entered().value)
              ? (entered().value as string[]).join(", ")
              : String(entered().value)}
          </span>
        </Show>
      )}
    </Show>
  );
};
